import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import { axiosInstance as axios, setSession } from '../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    if (user.business) localStorage.setItem('business', user.business.id);
    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  hasPermissions: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  verifyResetPasswordCode: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  setSystemParameter: () => Promise.resolve(),
  updateProfile: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const token = window.localStorage.getItem('token');

        if (token) {
          setSession(token);

          const response = await axios.get('/auth/user');
          const user = response.data;

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const hasPermissions = (permissions, every = false) => {
    if (state.user) {
      if (permissions.length === 0) return true;
      if (every) return permissions.every((permission) => state.user.permissions.includes(permission));
      return permissions.some((permission) => state.user.permissions.includes(permission));
    }
    return false;
  };

  const login = async (email, password) => {
    setSession(null);
    const response = await axios.post('/auth/login', {
      email,
      password,
    });
    const { token, user } = response.data;

    setSession(token);
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post('/auth/forgot_password', { email });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const verifyResetPasswordCode = async (code) => {
    try {
      await axios.post('/auth/reset_password', { code });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const resetPassword = async (password, code) => {
    try {
      await axios.put('/auth/reset_password', { password, code });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const setSystemParameter = async (data) => {
    try {
      const response = await axios.post('/system/parameter', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.post('/auth/profile', data);
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        hasPermissions,
        login,
        logout,
        forgotPassword,
        verifyResetPasswordCode,
        resetPassword,
        setSystemParameter,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
